import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { toast } from 'sonner';
import { useSystemOptions } from '@/contexts/SystemOptionsContext';
import { useIntegration } from '@/providers/IntegrationProvider';
import { usePermissions } from '@/providers/PermissionsProvider';
import {
  ArrowLeft,
  AlertCircle,
  Copy,
  MoreVertical,
  Trash2,
  TrendingDown,
  Printer,
  FileEdit,
  CheckCircle2,
  Settings2,
  ArrowRight,
  Save,
  FileText,
} from 'lucide-react';
import { AssetDetailHeader } from '@/components/assets/detail/AssetDetailHeader';
import { AssetDetailOverview } from '@/components/assets/detail/AssetDetailOverview';
import { AssetDetailValues } from '@/components/assets/detail/AssetDetailValues';
import { AssetDetailSettings } from '@/components/assets/detail/AssetDetailSettings';
import { AssetDetailSupplier } from '@/components/assets/detail/AssetDetailSupplier';
import { AssetDetailEquipment } from '@/components/assets/detail/AssetDetailEquipment';
import { AssetDetailCustomFields } from '@/components/assets/detail/AssetDetailCustomFields';
import { AssetDetailNotes } from '@/components/assets/detail/AssetDetailNotes';
import { AssetDetailCompliance } from '@/components/assets/detail/AssetDetailCompliance';
import { AssetDetailHistory } from '@/components/assets/detail/AssetDetailHistory';
import { DocumentsTab } from '@/components/documents/documents-tab';
import { AssetAlertsTab } from '@/components/assets/asset-alerts-tab';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { dataService } from '@/services/data-service';
import type { Asset } from '@/types/asset';
import { generateAlertsForAsset, calculateAlertSummary } from '@/services/alerts-service';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DisposalWizard } from '@/components/assets/disposal-wizard';
import { LabelPrintingModal } from '@/components/assets/label-printing-modal';
import { exportAssetDetailToPDF, downloadAssetPDF } from '@/lib/asset-pdf-export';

// Helper function to validate a required string field - moved outside component to reduce cognitive complexity (S3776)
function validateRequiredString(
  value: string | undefined, 
  fieldName: string, 
  errorMessage: string, 
  errors: Record<string, string>
): void {
  if (!value || value.trim() === '') {
    errors[fieldName] = errorMessage;
  }
}

// Helper function to validate acquisition cost - moved outside component to reduce cognitive complexity (S3776)
function validateAcquisitionCost(
  cost: number | undefined | null, 
  errors: Record<string, string>
): void {
  if (cost === undefined || cost === null || cost === 0) {
    errors.acquisitionCost = 'Acquisition value is required and must be greater than 0';
    return;
  }
  if (cost < 0) {
    errors.acquisitionCost = 'Acquisition cost cannot be negative';
  }
}

// Required fields for financials integration validation - moved outside component to reduce cognitive complexity (S3776)
const FINANCIALS_REQUIRED_FIELDS: ReadonlyArray<{field: keyof Asset; label: string}> = [
  { field: 'assetCostNominal', label: 'Asset Cost Nominal' },
  { field: 'depreciationBSNominal', label: 'Depreciation B/S Nominal' },
  { field: 'depreciationPLNominal', label: 'Depreciation P&L Nominal' },
  { field: 'disposalsNominal', label: 'Disposals Nominal' },
  { field: 'revaluationBSNominal', label: 'Revaluation B/S Nominal' },
  { field: 'revaluationPLNominal', label: 'Revaluation P&L Nominal' },
  { field: 'disposalsBankAccountNominal', label: 'Disposals Bank Account Nominal' },
  { field: 'additionsWIPBankAccountNominal', label: 'Additions WIP / Bank Account Nominal' },
  { field: 'project', label: 'Project' },
  { field: 'costCentre', label: 'Cost Centre' },
];

// Helper function to validate financials integration fields - moved outside component to reduce cognitive complexity (S3776)
function validateFinancialsFields(
  asset: Asset, 
  errors: Record<string, string>
): void {
  for (const { field, label } of FINANCIALS_REQUIRED_FIELDS) {
    const value = asset[field];
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      errors[field] = `${label} is required`;
    }
  }
}

// Field-to-tab mapping for error detection - moved outside component to reduce cognitive complexity (S3776)
const TAB_FIELD_MAPPING: Record<string, string[]> = {
  overview: ['code', 'name', 'category', 'location', 'acquisitionDate'],
  values: ['acquisitionCost'],
  settings: [
    'assetCostNominal', 'depreciationBSNominal', 'depreciationPLNominal',
    'disposalsNominal', 'revaluationBSNominal', 'revaluationPLNominal',
    'disposalsBankAccountNominal', 'additionsWIPBankAccountNominal',
    'project', 'costCentre'
  ],
};

// Helper function to determine which tabs have errors - moved outside component to reduce cognitive complexity (S3776)
function calculateTabsWithErrors(errorObj: Record<string, string>): Set<string> {
  const tabsWithErrors = new Set<string>();
  const errorKeys = Object.keys(errorObj);

  for (const [tab, fields] of Object.entries(TAB_FIELD_MAPPING)) {
    if (fields.some(field => errorKeys.includes(field))) {
      tabsWithErrors.add(tab);
    }
  }

  return tabsWithErrors;
}

// External validation function - moved outside component to reduce cognitive complexity (S3776)
function validateRequiredFieldsExternal(
  assetToValidate: Asset,
  isAutoNumberingEnabled: boolean,
  hasFinancialsIntegration: boolean
): Record<string, string> {
  const newErrors: Record<string, string> = {};

  // Validate asset code (unless auto-numbering is enabled)
  if (!assetToValidate.code && !isAutoNumberingEnabled) {
    newErrors.code = 'Asset code is required';
  }

  validateRequiredString(assetToValidate.name, 'name', 'Description is required', newErrors);
  validateRequiredString(assetToValidate.category, 'category', 'Category is required', newErrors);
  validateRequiredString(assetToValidate.location, 'location', 'Location is required', newErrors);
  validateRequiredString(assetToValidate.acquisitionDate, 'acquisitionDate', 'Acquisition date is required', newErrors);
  validateAcquisitionCost(assetToValidate.acquisitionCost, newErrors);

  if (hasFinancialsIntegration) {
    validateFinancialsFields(assetToValidate, newErrors);
  }

  return newErrors;
}

// Helper to create a new empty asset - moved outside component to reduce cognitive complexity (S3776)
function createNewAsset(): Asset {
  return {
    id: `new-${Date.now()}`,
    code: '',
    name: '',
    description: '',
    category: 'Furniture & Fixtures',
    status: 'active',
    location: '',
    acquisitionCost: 0,
    netBookValue: 0,
    accumulatedDepreciation: 0,
    currency: 'GBP',
    acquisitionDate: new Date().toISOString().split('T')[0],
    serialNumber: '',
    model: '',
    manufacturer: '',
    createdBy: '',
    createdDate: new Date().toISOString(),
    depreciationMethod: 'Straight Line',
    baseValueOn: 'remaining',
  };
}

// Field label mapping - moved outside component to reduce cognitive complexity (S3776)
const FIELD_LABELS: Record<string, string> = {
  code: 'Asset Code',
  name: 'Description',
  description: 'Additional Description',
  location: 'Location',
  subLocation: 'Sub Location',
  category: 'Category',
  subCategory: 'Sub Category',
  status: 'Status',
  acquisitionCost: 'Acquisition Value',
  netBookValue: 'Net Book Value',
  accumulatedDepreciation: 'Accumulated Depreciation',
  residualValue: 'Residual Value',
  usefulLife: 'Useful Life',
  currency: 'Currency',
  quantity: 'Quantity',
  acquisitionDate: 'Acquisition Date',
  disposalDate: 'Disposal Date',
  depreciationMethod: 'Depreciation Method',
  postingFrequency: 'Posting Frequency',
  depreciationPercentage: 'Depreciation Percentage',
  depreciationYears: 'Depreciation Years',
  percentage: 'Percentage',
  years: 'Years',
  inServicePeriod: 'In Service Period',
  inServiceYear: 'In Service Year',
  nextPostingPeriod: 'Next Posting Period',
  nextPostingYear: 'Next Posting Year',
  postingsMade: 'Postings Made',
  totalPostings: 'Total Postings',
  remainingPostings: 'Remaining Postings',
  originalPostings: 'Original Postings',
  doNotDepreciate: 'Do Not Depreciate',
  baseValueOn: 'Base Value On',
  currentBaseValue: 'Current Base Value',
  assetCostNominal: 'Asset Cost Nominal',
  assetCostNominalName: 'Asset Cost Nominal Name',
  depreciationBSNominal: 'Depreciation B/S Nominal',
  depreciationBSNominalName: 'Depreciation B/S Nominal Name',
  depreciationPLNominal: 'Depreciation P&L Nominal',
  depreciationPLNominalName: 'Depreciation P&L Nominal Name',
  disposalsNominal: 'Disposals Nominal',
  disposalsNominalName: 'Disposals Nominal Name',
  revaluationBSNominal: 'Revaluation B/S Nominal',
  revaluationBSNominalName: 'Revaluation B/S Nominal Name',
  revaluationPLNominal: 'Revaluation P&L Nominal',
  revaluationPLNominalName: 'Revaluation P&L Nominal Name',
  disposalsBankAccountNominal: 'Disposals Bank Account Nominal',
  disposalsBankAccountNominalName: 'Disposals Bank Account Nominal Name',
  additionsWIPBankAccountNominal: 'Additions WIP / Bank Account Nominal',
  additionsWIPBankAccountNominalName: 'Additions WIP / Bank Account Nominal Name',
  project: 'Project',
  projectName: 'Project Name',
  costCentre: 'Cost Centre',
  costCentreName: 'Cost Centre Name',
  insuranceProvider: 'Insurance Provider',
  insurancePolicyNumber: 'Insurance Policy Number',
  insuranceValue: 'Insurance Value',
  insuranceStartDate: 'Insurance Start Date',
  insuranceExpiryDate: 'Insurance Expiry Date',
  insuranceNotes: 'Insurance Notes',
  warrantyProvider: 'Warranty Provider',
  warrantyStartDate: 'Warranty Start Date',
  warrantyExpiryDate: 'Warranty Expiry Date',
  warrantyExpiry: 'Warranty Expiry',
  warrantyTerms: 'Warranty Terms',
  warrantyCost: 'Warranty Cost',
  warrantyCertificationUrl: 'Warranty Certification',
  warrantyNotes: 'Warranty Notes',
  supplierId: 'Supplier ID',
  supplierName: 'Supplier Name',
  supplier: 'Supplier',
  supplierReference: 'Supplier Reference',
  supplierContact: 'Supplier Contact',
  supplierPhone: 'Supplier Phone',
  supplierEmail: 'Supplier Email',
  purchaseOrderNumber: 'Purchase Order Number',
  invoiceReference: 'Invoice Reference',
  hirePurchase: 'Hire Purchase',
  hirePurchaseEndDate: 'Hire Purchase End Date',
  maintenanceSchedule: 'Maintenance Schedule',
  serialNumber: 'Serial Number',
  model: 'Model',
  manufacturer: 'Manufacturer',
  barcode: 'Barcode',
  barcodeType: 'Barcode Type',
  barcodeValue: 'Barcode Value',
  registrationNumber: 'Registration Number',
  assignedTo: 'Assigned To',
  assignedDate: 'Assigned Date',
  userValue1: 'User Value 1',
  userValue2: 'User Value 2',
  userValue3: 'User Value 3',
  userValue4: 'User Value 4',
  userValue5: 'User Value 5',
  userKey1: 'User Key 1',
  userKey2: 'User Key 2',
  userKey3: 'User Key 3',
  userKey4: 'User Key 4',
  userKey5: 'User Key 5',
  userDate1: 'User Date 1',
  userDate2: 'User Date 2',
  userDate3: 'User Date 3',
  notes: 'Notes',
  tags: 'Tags',
  resourceCode: 'Resource Code',
  subLedger: 'Sub Ledger',
};

// Field-to-section mapping - moved outside component to reduce cognitive complexity (S3776)
const FIELD_SECTIONS: Record<string, string> = {
  code: 'Overview', name: 'Overview', description: 'Overview', location: 'Overview',
  subLocation: 'Overview', category: 'Overview', subCategory: 'Overview', status: 'Overview',
  acquisitionCost: 'Values', currency: 'Values', quantity: 'Values', acquisitionDate: 'Values',
  disposalDate: 'Values', netBookValue: 'Values', accumulatedDepreciation: 'Values',
  residualValue: 'Values', usefulLife: 'Values',
  depreciationMethod: 'Settings', postingFrequency: 'Settings', depreciationPercentage: 'Settings',
  depreciationYears: 'Settings', percentage: 'Settings', years: 'Settings',
  inServicePeriod: 'Settings', inServiceYear: 'Settings', nextPostingPeriod: 'Settings',
  nextPostingYear: 'Settings', postingsMade: 'Settings', totalPostings: 'Settings',
  remainingPostings: 'Settings', originalPostings: 'Settings', doNotDepreciate: 'Settings',
  baseValueOn: 'Settings', currentBaseValue: 'Settings', assetCostNominal: 'Settings',
  assetCostNominalName: 'Settings', depreciationBSNominal: 'Settings', depreciationBSNominalName: 'Settings',
  depreciationPLNominal: 'Settings', depreciationPLNominalName: 'Settings', disposalsNominal: 'Settings',
  disposalsNominalName: 'Settings', revaluationBSNominal: 'Settings', revaluationBSNominalName: 'Settings',
  revaluationPLNominal: 'Settings', revaluationPLNominalName: 'Settings',
  disposalsBankAccountNominal: 'Settings', disposalsBankAccountNominalName: 'Settings',
  additionsWIPBankAccountNominal: 'Settings', additionsWIPBankAccountNominalName: 'Settings',
  project: 'Settings', projectName: 'Settings', costCentre: 'Settings', costCentreName: 'Settings',
  insuranceProvider: 'Compliance', insurancePolicyNumber: 'Compliance', insuranceValue: 'Compliance',
  insuranceStartDate: 'Compliance', insuranceExpiryDate: 'Compliance', insuranceNotes: 'Compliance',
  warrantyProvider: 'Compliance', warrantyStartDate: 'Compliance', warrantyExpiryDate: 'Compliance',
  warrantyExpiry: 'Compliance', warrantyTerms: 'Compliance', warrantyCost: 'Compliance',
  warrantyCertificationUrl: 'Compliance', warrantyNotes: 'Compliance',
  supplierId: 'Supplier', supplierName: 'Supplier', supplier: 'Supplier',
  supplierReference: 'Supplier', supplierContact: 'Supplier', supplierPhone: 'Supplier',
  supplierEmail: 'Supplier', purchaseOrderNumber: 'Supplier', invoiceReference: 'Supplier',
  hirePurchase: 'Supplier', hirePurchaseEndDate: 'Supplier', maintenanceSchedule: 'Supplier',
  serialNumber: 'Equipment', model: 'Equipment', manufacturer: 'Equipment', barcode: 'Equipment',
  barcodeType: 'Equipment', barcodeValue: 'Equipment', registrationNumber: 'Equipment',
  assignedTo: 'Equipment', assignedDate: 'Equipment',
  userValue1: 'Custom Fields', userValue2: 'Custom Fields', userValue3: 'Custom Fields',
  userValue4: 'Custom Fields', userValue5: 'Custom Fields', userKey1: 'Custom Fields',
  userKey2: 'Custom Fields', userKey3: 'Custom Fields', userKey4: 'Custom Fields',
  userKey5: 'Custom Fields', userDate1: 'Custom Fields', userDate2: 'Custom Fields',
  userDate3: 'Custom Fields', notes: 'Notes', tags: 'Other', resourceCode: 'Other', subLedger: 'Other',
};

// Fields to exclude from comparison - moved outside component to reduce cognitive complexity (S3776)
const EXCLUDED_FIELDS = new Set([
  'id', 'createdBy', 'createdDate', 'modifiedBy', 'modifiedDate', 'used',
  'priceRecordLink', 'photos', 'attachments', 'linkedAssets', 'linkedEquipment',
  'parentAssetId', 'alertSummary', 'importJobId',
]);

// Long text fields that need special handling - moved outside component to reduce cognitive complexity (S3776)
const LONG_TEXT_FIELDS = ['notes', 'insuranceNotes', 'warrantyNotes', 'warrantyTerms'];

// Section order for structured changes - moved outside component to reduce cognitive complexity (S3776)
const SECTION_ORDER = ['Overview', 'Values', 'Settings', 'Compliance', 'Supplier', 'Equipment', 'Custom Fields', 'Notes', 'Other'];

// Helper function to format values - moved outside component to reduce cognitive complexity (S3776)
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return 'Not set';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'string') return value || 'Not set';
  if (typeof value === 'object' && value !== null && 'name' in value) return String((value as {name: string}).name);
  if (Array.isArray(value)) return value.length > 0 ? `${value.length} items` : 'Not set';
  return String(value);
}

// Helper function to get proper field label - moved outside component to reduce cognitive complexity (S3776)
function formatFieldName(key: string): string {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase()).trim();
}

// Type definitions for change tracking
type FieldChange = {field: string; oldValue: string; newValue: string};
type SectionChanges = {section: string; changes: FieldChange[]};

// Helper to calculate text field length - extracts nested ternary (S3358)
function calculateTextFieldLength(
  changeValue: string,
  asset: Asset | null,
  fieldKey: string
): number {
  if (changeValue === 'Not set') return 0;
  if (!asset) return 0;
  const fieldValue = asset[fieldKey as keyof Asset] as string;
  return fieldValue?.length || 0;
}

// Helper to update change labels for long text fields - reduces cognitive complexity (S3776)
function updateLongTextFieldChangeLabels(
  change: FieldChange,
  oldLength: number,
  newLength: number
): void {
  if (oldLength === 0 && newLength > 0) {
    change.newValue = 'Added';
    change.oldValue = 'Not set';
  } else if (oldLength > 0 && newLength === 0) {
    change.newValue = 'Removed';
    change.oldValue = 'Previously set';
  } else if (oldLength > 0 && newLength > 0) {
    change.newValue = 'Modified';
    change.oldValue = 'Previously set';
  }
}

// Helper to process a single change for structured changes - reduces cognitive complexity (S3776)
function processChangeForStructuredChanges(
  change: FieldChange,
  editedAsset: Asset,
  initialAsset: Asset,
  sectionMap: Map<string, FieldChange[]>
): void {
  const fieldKey = Object.keys(editedAsset).find(key => FIELD_LABELS[key] === change.field);
  const section = fieldKey ? (FIELD_SECTIONS[fieldKey] || 'Other') : 'Other';
  
  if (fieldKey && LONG_TEXT_FIELDS.includes(fieldKey)) {
    const oldLength = calculateTextFieldLength(change.oldValue, initialAsset, fieldKey);
    const newLength = calculateTextFieldLength(change.newValue, editedAsset, fieldKey);
    updateLongTextFieldChangeLabels(change, oldLength, newLength);
  }
  
  if (!sectionMap.has(section)) sectionMap.set(section, []);
  sectionMap.get(section)!.push(change);
}

// Generate change summary - moved outside component to reduce cognitive complexity (S3776)
function generateChangeSummaryExternal(
  editedAsset: Asset | null,
  initialAsset: Asset | null
): FieldChange[] {
  if (!editedAsset || !initialAsset) return [];
  
  const changes: FieldChange[] = [];
  
  Object.keys(editedAsset).forEach((key) => {
    if (EXCLUDED_FIELDS.has(key)) return;
    
    const oldValue = initialAsset[key as keyof Asset];
    const newValue = editedAsset[key as keyof Asset];
    
    if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
      changes.push({
        field: formatFieldName(key),
        oldValue: formatValue(oldValue),
        newValue: formatValue(newValue),
      });
    }
  });
  
  return changes;
}

// Generate structured changes - moved outside component to reduce cognitive complexity (S3776)
function generateStructuredChangesExternal(
  editedAsset: Asset | null,
  initialAsset: Asset | null,
  changes: FieldChange[]
): SectionChanges[] {
  if (!editedAsset || !initialAsset) return [];
  
  const sectionMap = new Map<string, FieldChange[]>();
  
  // Use helper function to process each change - reduces cognitive complexity (S3776)
  changes.forEach((change) => {
    processChangeForStructuredChanges(change, editedAsset, initialAsset, sectionMap);
  });
  
  const structured: SectionChanges[] = [];
  SECTION_ORDER.forEach(section => {
    const sectionChanges = sectionMap.get(section);
    if (sectionChanges && sectionChanges.length > 0) {
      structured.push({ section, changes: sectionChanges });
    }
  });
  
  return structured;
}

// External helper to check if asset has changes - reduces cognitive complexity (S3776)
function hasAssetChanges(
  isEditMode: boolean,
  editedAsset: Asset | null,
  asset: Asset | null,
  isCreateMode: boolean
): boolean {
  if (!isEditMode || !editedAsset || !asset) return false;
  if (isCreateMode) return true;
  return JSON.stringify(editedAsset) !== JSON.stringify(asset);
}

// External helper to calculate alerts count - reduces cognitive complexity (S3776)
function calculateAlertsCount(currentAsset: Asset | null): number {
  if (!currentAsset) return 0;
  const alerts = generateAlertsForAsset(currentAsset);
  const summary = calculateAlertSummary(alerts);
  return summary.totalCount;
}

// External helper to pluralize words - reduces cognitive complexity (S3776)
function pluralizeWord(count: number, singular: string): string {
  return count === 1 ? singular : `${singular}s`;
}

// External helpers for save button text - separate methods to avoid boolean parameter (S2301)
function getSaveButtonTextSaving(): string {
  return 'Saving...';
}

function getSaveButtonTextCreate(): string {
  return 'Create';
}

function getSaveButtonTextEdit(): string {
  return 'Save';
}

// Combined helper to get save button text based on state - avoids boolean selector in JSX
function determineSaveButtonText(isSaving: boolean, isCreateMode: boolean): string {
  if (isSaving) return getSaveButtonTextSaving();
  if (isCreateMode) return getSaveButtonTextCreate();
  return getSaveButtonTextEdit();
}

// External helpers for dialog title - separate methods to avoid boolean parameter (S2301)
function getCreateDialogTitle(): string {
  return 'Confirm Asset Creation';
}

function getEditDialogTitle(): string {
  return 'Confirm Asset Changes';
}

// External helpers for dialog description - separate methods to avoid boolean parameter (S2301)
function getCreateDialogDescription(): string {
  return 'Review the details of the asset you\'re about to create.';
}

function getEditDialogDescription(): string {
  return 'Review the changes you\'re about to save to this asset.';
}

// External helpers for confirm button text - separate methods to avoid boolean parameter (S2301)
function getConfirmButtonTextSaving(): string {
  return 'Saving...';
}

function getConfirmButtonTextCreate(): string {
  return 'Create Asset';
}

function getConfirmButtonTextEdit(): string {
  return 'Confirm & Save';
}

// Combined helper to get confirm button text based on state - avoids boolean selector in JSX
function determineConfirmButtonText(isSaving: boolean, isCreateMode: boolean): string {
  if (isSaving) return getConfirmButtonTextSaving();
  if (isCreateMode) return getConfirmButtonTextCreate();
  return getConfirmButtonTextEdit();
}

// External helper to get tab error class - reduces cognitive complexity (S3776)
function getTabErrorClass(hasError: boolean, isEditMode: boolean): string {
  const baseClass = 'relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:transition-all data-[state=active]:after:opacity-100 after:opacity-0';
  if (hasError && isEditMode) {
    return `${baseClass} bg-destructive/10 after:bg-destructive dark:data-[state=active]:after:bg-destructive`;
  }
  return `${baseClass} after:bg-primary dark:data-[state=active]:after:bg-primary`;
}

// External helpers for changes summary text - separate methods to avoid boolean parameter (S2301)
function getCreateChangesSummaryText(changesCount: number, pluralize: (count: number, word: string) => string): string {
  return `Creating new asset with ${changesCount} ${pluralize(changesCount, 'field')}`;
}

function getEditChangesSummaryText(changesCount: number, sectionsCount: number, pluralize: (count: number, word: string) => string): string {
  return `${changesCount} ${pluralize(changesCount, 'field')} modified across ${sectionsCount} ${pluralize(sectionsCount, 'section')}`;
}

// Helper to determine if a field should show simple "modified" message - reduces cognitive complexity (S3776)
function shouldShowSimpleModifiedMessage(fieldName: string, isCreateMode: boolean, newValue: string): boolean {
  const notesFields = ['Notes', 'Insurance Notes', 'Warranty Notes', 'Warranty Terms'];
  return notesFields.includes(fieldName) && !isCreateMode && newValue === 'Modified';
}

// Helper to get dialog title for create mode - avoids boolean selector (S2301)
function getDialogTitleForCreate(): string {
  return getCreateDialogTitle();
}

// Helper to get dialog title for edit mode - avoids boolean selector (S2301)
function getDialogTitleForEdit(): string {
  return getEditDialogTitle();
}

// Helper to get dialog description for create mode - avoids boolean selector (S2301)
function getDialogDescriptionForCreate(): string {
  return getCreateDialogDescription();
}

// Helper to get dialog description for edit mode - avoids boolean selector (S2301)
function getDialogDescriptionForEdit(): string {
  return getEditDialogDescription();
}

// Helper to get changes summary text for create mode - avoids boolean selector (S2301)
function getChangesSummaryTextForCreate(changesCount: number, pluralize: (count: number, word: string) => string): string {
  return getCreateChangesSummaryText(changesCount, pluralize);
}

// Helper to get changes summary text for edit mode - avoids boolean selector (S2301)
function getChangesSummaryTextForEdit(changesCount: number, sectionsCount: number, pluralize: (count: number, word: string) => string): string {
  return getEditChangesSummaryText(changesCount, sectionsCount, pluralize);
}

// Helper component to render field change value for CREATE mode - avoids boolean selector (S2301)
function FieldChangeValueForCreate({ 
  fieldChange 
}: Readonly<{ 
  fieldChange: { field: string; oldValue: string; newValue: string }; 
}>): React.ReactElement {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-muted-foreground">Value:</span>
      <code className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-mono font-medium">
        {fieldChange.newValue}
      </code>
    </div>
  );
}

// Helper component to render field change value for EDIT mode - avoids boolean selector (S2301)
function FieldChangeValueForEdit({
  fieldChange 
}: Readonly<{ 
  fieldChange: { field: string; oldValue: string; newValue: string }; 
}>): React.ReactElement {
  return (
    <div className="flex items-center gap-2 text-xs">
      <div className="flex-1 flex items-center gap-1.5">
        <span className="text-muted-foreground">From:</span>
        <code className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">
          {fieldChange.oldValue}
        </code>
      </div>
      <ArrowRight className="h-3 w-3 text-primary flex-shrink-0" />
      <div className="flex-1 flex items-center gap-1.5">
        <span className="text-muted-foreground">To:</span>
        <code className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-mono font-medium">
          {fieldChange.newValue}
        </code>
      </div>
    </div>
  );
}

// Helper component to render simple modified message - avoids boolean selector (S2301)
function FieldChangeSimpleMessage({
  fieldChange 
}: Readonly<{ 
  fieldChange: { field: string; oldValue: string; newValue: string }; 
}>): React.ReactElement {
  return (
    <div className="text-xs text-muted-foreground">
      {fieldChange.field} were modified
    </div>
  );
}

// Helper component to render field change content for CREATE mode - avoids boolean selector (S2301)
function FieldChangeContentForCreate({
  fieldChange, 
  showSimpleMessage 
}: Readonly<{ 
  fieldChange: { field: string; oldValue: string; newValue: string }; 
  showSimpleMessage: boolean;
}>): React.ReactElement {
  if (showSimpleMessage) {
    return <FieldChangeSimpleMessage fieldChange={fieldChange} />;
  }
  
  return (
    <>
      <div className="text-xs font-medium text-foreground">
        {fieldChange.field}
      </div>
      <FieldChangeValueForCreate fieldChange={fieldChange} />
    </>
  );
}

// Helper component to render field change content for EDIT mode - avoids boolean selector (S2301)
function FieldChangeContentForEdit({
  fieldChange, 
  showSimpleMessage 
}: Readonly<{ 
  fieldChange: { field: string; oldValue: string; newValue: string }; 
  showSimpleMessage: boolean;
}>): React.ReactElement {
  if (showSimpleMessage) {
    return <FieldChangeSimpleMessage fieldChange={fieldChange} />;
  }
  
  return (
    <>
      <div className="text-xs font-medium text-foreground">
        {fieldChange.field}
      </div>
      <FieldChangeValueForEdit fieldChange={fieldChange} />
    </>
  );
}

// Helper to compute permission flags - reduces cognitive complexity (S3776)
function computePermissionFlags(
  permissionsLoading: boolean,
  hasSubPermission: (permission: string) => boolean
): { canEditAssets: boolean; canCreateAssets: boolean; canDeleteAssets: boolean } {
  return {
    canEditAssets: !permissionsLoading && hasSubPermission('edit-assets'),
    canCreateAssets: !permissionsLoading && hasSubPermission('create-assets'),
    canDeleteAssets: !permissionsLoading && hasSubPermission('delete-assets'),
  };
}

export default function AssetDetail(){
  const { assetId } = useParams<{ assetId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast: legacyToast } = useToast();
  const { options: systemOptions } = useSystemOptions();
  const { settings: integrationSettings } = useIntegration();
  const { hasSubPermission, loading: permissionsLoading } = usePermissions();

  // Permission checks for asset actions - uses external helper to reduce cognitive complexity (S3776)
  const { canEditAssets, canCreateAssets, canDeleteAssets } = computePermissionFlags(permissionsLoading, hasSubPermission);

  const [asset, setAsset] = useState<Asset | null>(null);
  const [editedAsset, setEditedAsset] = useState<Asset | null>(null);
  const [originalPhotos, setOriginalPhotos] = useState<Asset['photos']>(undefined);
  const [initialAsset, setInitialAsset] = useState<Asset | null>(null); // Store initial state for create mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDisposalWizard, setShowDisposalWizard] = useState(false);
  const [showLabelPrintingModal, setShowLabelPrintingModal] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Array<{field: string; oldValue: string; newValue: string}>>([]);
  
  // Structured changes for better organization in save dialog
  type FieldChange = {field: string; oldValue: string; newValue: string};
  type SectionChanges = {section: string; changes: FieldChange[]};
  const [structuredChanges, setStructuredChanges] = useState<SectionChanges[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Check if we're in create mode
  const isCreateMode = searchParams.get('mode') === 'create';

  // Helper to check if auto-numbering is enabled
  const isAutoNumberingEnabled = isCreateMode && systemOptions?.autoNumbering.enabled;

  // Helper to check if financials integration is enabled
  const hasFinancialsIntegration = integrationSettings?.enabled && 
    integrationSettings?.integrationType === 'access-financials';

  // Wrapper that calls external validation function - reduces cognitive complexity (S3776)
  const validateRequiredFields = (assetToValidate: Asset): Record<string, string> => 
    validateRequiredFieldsExternal(assetToValidate, !!isAutoNumberingEnabled, !!hasFinancialsIntegration);

  // Determine which tabs have errors - uses external calculateTabsWithErrors function
  const getTabsWithErrors = useMemo(() => {
    if (!isEditMode || !editedAsset) return new Set<string>();
    if (Object.keys(errors).length === 0) return new Set<string>();
    return calculateTabsWithErrors(errors);
  }, [isEditMode, editedAsset, errors]);

  // Initialize asset state based on mode
  useEffect(() => {
    if (isCreateMode) {
      const newAsset = createNewAsset();
      setAsset(newAsset);
      setEditedAsset(structuredClone(newAsset));
      setInitialAsset(structuredClone(newAsset));
      setIsEditMode(true);
      setLoading(false);
    } else {
      loadAssetDetail();
    }
    
    return () => {
      setAsset(null);
      setEditedAsset(null);
      setInitialAsset(null);
      setIsEditMode(false);
      setErrors({});
    };
  }, [assetId, isCreateMode]);

  const loadAssetDetail = async () => {
    if (!assetId) return;

    setLoading(true);
    try {
      const data = await dataService.getAsset(assetId);
      setAsset(data);
    } catch (error) {
      console.error('Failed to load asset:', error);
      legacyToast({
        title: 'Error',
        description: 'Failed to load asset details',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    // Create a deep copy of the asset to avoid mutating the original
    setEditedAsset(asset ? structuredClone(asset) : null);
    // Store initial asset state for comparison
    setInitialAsset(asset ? structuredClone(asset) : null);
    // Store original photos for incremental image updates
    setOriginalPhotos(asset?.photos ? [...asset.photos] : undefined);
    setIsEditMode(true);
  };

  // Helper to reset edit state - extracted to reduce cognitive complexity (S3776)
  const resetEditState = () => {
    setEditedAsset(null);
    setInitialAsset(null);
    setOriginalPhotos(undefined);
    setIsEditMode(false);
    setErrors({});
  };

  // Helper to handle navigation or state reset based on mode - simplified to reduce cognitive complexity (S3776)
  const exitEditMode = (includePhotosReset: boolean) => {
    if (isCreateMode) { navigate(-1); return; }
    if (includePhotosReset) { resetEditState(); return; }
    setIsEditMode(false);
    setEditedAsset(null);
    setInitialAsset(null);
    setErrors({});
  };

  // Simplified cancel handler - reduces cognitive complexity (S3776)
  const handleCancelEdit = () => {
    const hasNoChanges = generateChangeSummary().length === 0;
    if (hasNoChanges) { exitEditMode(false); return; }
    setShowCancelDialog(true);
  };

  const confirmCancelEdit = () => {
    exitEditMode(true);
    setShowCancelDialog(false);
  };

  // Wrapper functions that call external helpers - reduces cognitive complexity (S3776)
  const generateChangeSummary = () => generateChangeSummaryExternal(editedAsset, initialAsset);
  const generateStructuredChanges = () => {
    const changes = generateChangeSummary();
    return generateStructuredChangesExternal(editedAsset, initialAsset, changes);
  };

  // Simplified save handler - reduces cognitive complexity (S3776)
  const handleSave = () => {
    if (!editedAsset) return;
    const validationErrors = validateRequiredFields(editedAsset);
    const hasErrors = Object.keys(validationErrors).length > 0;
    if (hasErrors) { setErrors(validationErrors); return; }
    setErrors({});
    setPendingChanges(generateChangeSummary());
    setStructuredChanges(generateStructuredChanges());
    setShowSaveDialog(true);
  };

  const confirmSave = async () => {
    if (!editedAsset) return;
    setIsSaving(true);
    try {
      if (isCreateMode) {
        // Create new asset
        const createdAsset = await dataService.createAsset(editedAsset);
        setAsset(createdAsset);
        legacyToast({
          title: 'Success',
          description: 'Asset created successfully',
        });
        // Navigate to the new asset's detail page
        navigate(`/assets/${createdAsset.id}`, { replace: true });
      } else {
        // Update existing asset with incremental image updates
        // Pass originalPhotos to enable add/remove individual images without affecting others
        const updatedAsset = await dataService.updateAsset(editedAsset.id, editedAsset, originalPhotos);
        setAsset(updatedAsset);
        legacyToast({
          title: 'Success',
          description: 'Asset saved successfully',
        });
        // Stay on the asset detail page after saving
      }
      setIsEditMode(false);
      setEditedAsset(null);
      setInitialAsset(null);
      setOriginalPhotos(undefined);
      setShowSaveDialog(false);
    } catch (error) {
      console.error('Failed to save asset:', error);
      legacyToast({
        title: 'Error',
        description: 'Failed to save asset. Please try again.',
        variant: 'destructive',
      });
      setShowSaveDialog(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicate = () => {
    if (!asset) return;
    const duplicatedAsset = {
      ...asset,
      id: `new-${Date.now()}`,
      code: `${asset.code}-COPY`,
    };
    navigate('/assets/new?mode=create', { state: { asset: duplicatedAsset } });
  };

  const handleDelete = async () => {
    if (!asset) return;

    try {
      await dataService.deleteAsset(asset.id);
      legacyToast({
        title: 'Success',
        description: 'Asset deleted successfully',
      });
      navigate(-1);
    } catch (error) {
      console.error('Failed to delete asset:', error);
      legacyToast({
        title: 'Error',
        description: 'Failed to delete asset. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDisposal = () => {
    setShowDisposalWizard(true);
  };

  const handlePrintLabel = () => {
    setShowLabelPrintingModal(true);
  };

  const handleExportPDF = () => {
    if (!asset) return;

    try {
      const pdf = exportAssetDetailToPDF({ asset });
      downloadAssetPDF(pdf, asset.code);
      toast.success('Asset detail exported to PDF');
    } catch (error) {
      console.error('Failed to export PDF:', error);
      toast.error('Failed to export PDF');
    }
  };

  // Simplified asset update handler - reduces cognitive complexity (S3776)
  const handleAssetUpdate = (updatedAsset: Asset) => {
    const cloned = structuredClone(updatedAsset);
    if (isEditMode) { setEditedAsset(cloned); setErrors({}); }
    else { setAsset(cloned); }
  };

  // Calculate current asset and alerts count BEFORE any conditional returns
  const currentAsset = isEditMode ? editedAsset : asset;
  // Calculate alerts count - uses external helper to reduce cognitive complexity (S3776)
  const alertsCount = useMemo(() => calculateAlertsCount(currentAsset), [currentAsset]);

  // Check if asset has been modified - uses external helper to reduce cognitive complexity (S3776)
  const hasChanges = useMemo(
    () => hasAssetChanges(isEditMode, editedAsset, asset, isCreateMode),
    [isEditMode, editedAsset, asset, isCreateMode]
  );

  // Use external pluralize function to reduce cognitive complexity (S3776)
  const pluralize = pluralizeWord;

  if (loading) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <div className="border-b bg-card p-6">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-6 w-96" />
        </div>
        <div className="flex-1 flex">
          <div className="flex-1 p-6">
            <Skeleton className="h-64" />
          </div>
        </div>
      </div>
    );
  }

  if (!asset && !isCreateMode) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Asset Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The asset you're looking for doesn't exist or has been deleted.
          </p>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Assets
          </Button>
        </div>
      </div>
    );
  }

  if (!currentAsset) return null;

  const documentsCount = 0; // TODO: Get from asset data
  const photosCount = 0; // TODO: Get from asset data

  return (
    <>
      <div className="flex h-full flex-col" data-testid="asset-detail-page">
        {/* Header */}
        <div className="border-b bg-card">
          <div className="px-6 py-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="h-8 w-8"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex-1">
                <AssetDetailHeader
                  asset={currentAsset}
                  onAssetUpdate={handleAssetUpdate}
                  isEditMode={isEditMode}
                  isCreateMode={isCreateMode}
                />
              </div>
              <div className="flex items-center gap-2">
                {!isCreateMode && (
                  <EvoFeatureFeedbackCollector feature="Asset Detail View" />
                )}
                {!isEditMode ? (
                  <>
                    {canEditAssets && (
                      <Button variant="outline" size="sm" onClick={handleEdit} data-testid="asset-edit-button">
                        Edit
                      </Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canCreateAssets && (
                          <DropdownMenuItem onClick={handleDuplicate}>
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate Asset
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={handlePrintLabel}>
                          <Printer className="h-4 w-4 mr-2" />
                          Print Label
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleExportPDF}>
                          <FileText className="h-4 w-4 mr-2" />
                          Export to PDF
                        </DropdownMenuItem>
                        {(canEditAssets || canDeleteAssets) && <DropdownMenuSeparator />}
                        {canEditAssets && (
                          <DropdownMenuItem onClick={handleDisposal}>
                            <TrendingDown className="h-4 w-4 mr-2" />
                            Dispose Asset
                          </DropdownMenuItem>
                        )}
                        {canDeleteAssets && (
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => setShowDeleteDialog(true)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Asset
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                ) : (
                  <>
                    <Button variant="outline" size="sm" onClick={handleCancelEdit} disabled={isSaving}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={isSaving || !hasChanges}>
                      {determineSaveButtonText(isSaving, isCreateMode)}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Tabs Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden" data-testid="asset-detail-tabs">
              {/* Tab Bar - Enhanced Enterprise Design */}
              <div className="border-b bg-[rgb(242,245,245)] dark:bg-muted/30">
                <TabsList className="h-auto p-0 bg-transparent border-0 gap-1" data-testid="asset-detail-tab-list">
                  <TabsTrigger
                    value="overview"
                    className={getTabErrorClass(getTabsWithErrors.has('overview'), isEditMode)}
                  >
                    <span className="flex items-center gap-2">
                      Overview
                      {getTabsWithErrors.has('overview') && isEditMode && (
                        <AlertCircle className="h-4 w-4 text-destructive" />
                      )}
                    </span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="values"
                    className={getTabErrorClass(getTabsWithErrors.has('values'), isEditMode)}
                  >
                    <span className="flex items-center gap-2">
                      Values
                      {getTabsWithErrors.has('values') && isEditMode && (
                        <AlertCircle className="h-4 w-4 text-destructive" />
                      )}
                    </span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="settings"
                    className={getTabErrorClass(getTabsWithErrors.has('settings'), isEditMode)}
                  >
                    <span className="flex items-center gap-2">
                      Settings
                      {getTabsWithErrors.has('settings') && isEditMode && (
                        <AlertCircle className="h-4 w-4 text-destructive" />
                      )}
                    </span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="compliance"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Compliance
                  </TabsTrigger>
                  <TabsTrigger
                    value="supplier"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Supplier
                  </TabsTrigger>
                  <TabsTrigger
                    value="equipment"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Equipment
                  </TabsTrigger>
                  <TabsTrigger
                    value="custom-fields"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Custom Fields
                  </TabsTrigger>
                  <TabsTrigger
                    value="notes"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Notes
                  </TabsTrigger>
                  {!isCreateMode && (
                    <>
                      <TabsTrigger
                        value="documents"
                        className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                      >
                        Documents
                        {documentsCount > 0 && (
                          <Badge variant="secondary" className="ml-2 bg-muted/50 text-foreground hover:bg-muted/50">
                            {documentsCount}
                          </Badge>
                        )}
                      </TabsTrigger>
                      <TabsTrigger
                        value="alerts"
                        className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                      >
                        Alerts
                        {alertsCount > 0 && (
                          <Badge variant="secondary" className="ml-2 bg-muted/50 text-foreground hover:bg-muted/50">
                            {alertsCount}
                          </Badge>
                        )}
                      </TabsTrigger>
                      <TabsTrigger
                        value="history"
                        className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                      >
                        History
                      </TabsTrigger>
                    </>
                  )}
                </TabsList>
              </div>

              <div className="flex-1 overflow-auto">
                <TabsContent value="overview" className="m-0 p-6">
                  <AssetDetailOverview
                    asset={currentAsset}
                    onAssetUpdate={handleAssetUpdate}
                    isEditMode={isEditMode}
                    errors={errors}
                  />
                </TabsContent>

                <TabsContent value="values" className="m-0 p-6">
                  <AssetDetailValues
                    asset={currentAsset}
                    onAssetUpdate={handleAssetUpdate}
                    isEditMode={isEditMode}
                    isCreateMode={isCreateMode}
                    errors={errors}
                  />
                </TabsContent>

                <TabsContent value="settings" className="m-0 p-6">
                  <AssetDetailSettings
                    asset={currentAsset}
                    onAssetUpdate={handleAssetUpdate}
                    isEditMode={isEditMode}
                    errors={errors}
                  />
                </TabsContent>

                <TabsContent value="compliance" className="m-0 p-6">
                  <AssetDetailCompliance
                    asset={currentAsset}
                    onAssetUpdate={handleAssetUpdate}
                    isEditMode={isEditMode}
                  />
                </TabsContent>

                <TabsContent value="supplier" className="m-0 p-6">
                  <AssetDetailSupplier
                    asset={currentAsset}
                    onAssetUpdate={handleAssetUpdate}
                    isEditMode={isEditMode}
                  />
                </TabsContent>

                <TabsContent value="equipment" className="m-0 p-6">
                  <AssetDetailEquipment
                    asset={currentAsset}
                    onAssetUpdate={handleAssetUpdate}
                    isEditMode={isEditMode}
                  />
                </TabsContent>

                <TabsContent value="custom-fields" className="m-0 p-6">
                  <AssetDetailCustomFields
                    asset={currentAsset}
                    onAssetUpdate={handleAssetUpdate}
                    isEditMode={isEditMode}
                  />
                </TabsContent>

                <TabsContent value="notes" className="m-0 p-6">
                  <AssetDetailNotes
                    asset={currentAsset}
                    onAssetUpdate={handleAssetUpdate}
                    isEditMode={isEditMode}
                  />
                </TabsContent>

                {!isCreateMode && (
                  <>
                    <TabsContent value="documents" className="m-0 p-6">
                      <DocumentsTab assetId={currentAsset.id} />
                    </TabsContent>

                    <TabsContent value="alerts" className="m-0 p-6">
                      <AssetAlertsTab assetId={currentAsset.id} assetName={currentAsset.name} asset={currentAsset} />
                    </TabsContent>

                    <TabsContent value="history" className="m-0 p-6">
                      <AssetDetailHistory asset={currentAsset} />
                    </TabsContent>
                  </>
                )}
              </div>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this asset? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Changes</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to discard your changes?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancelEdit} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save Confirmation Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <FileEdit className="h-5 w-5 text-primary" />
              {isCreateMode ? getDialogTitleForCreate() : getDialogTitleForEdit()}
            </DialogTitle>
            <DialogDescription>
              {isCreateMode ? getDialogDescriptionForCreate() : getDialogDescriptionForEdit()}
            </DialogDescription>
          </DialogHeader>

          {pendingChanges.length === 0 && !isCreateMode ? (
            <div className="flex flex-col items-center justify-center py-8 text-center px-6">
              <CheckCircle2 className="h-12 w-12 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">
                No changes detected
              </p>
            </div>
          ) : (
            <>
              <div className="px-6 pb-3 flex items-center justify-between flex-shrink-0 border-b">
                <p className="text-sm font-medium">
                  {isCreateMode ? getChangesSummaryTextForCreate(pendingChanges.length, pluralize) : getChangesSummaryTextForEdit(pendingChanges.length, structuredChanges.length, pluralize)}
                </p>
                <Badge variant="secondary">
                  {pendingChanges.length}
                </Badge>
              </div>

              <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4">
                <div className="space-y-4">
                  {structuredChanges.map((sectionChange) => (
                    <div key={sectionChange.section} className="space-y-3">
                      {/* Section Header */}
                      <div className="flex items-center gap-2 pb-2 border-b">
                        <Settings2 className="h-4 w-4 text-primary" />
                        <h3 className="font-semibold text-sm">{sectionChange.section}</h3>
                        <Badge variant="outline" className="ml-auto text-xs">
                          {sectionChange.changes.length} {pluralize(sectionChange.changes.length, 'change')}
                        </Badge>
                      </div>

                      {/* Field Changes - uses separate components for create/edit to avoid boolean selector (S2301) */}
                      <div className="space-y-2 pl-2">
                        {sectionChange.changes.map((fieldChange) => (
                          <div
                            key={fieldChange.field}
                            className="rounded-md border bg-card p-3 space-y-1.5"
                          >
                            {isCreateMode ? (
                              <FieldChangeContentForCreate 
                                fieldChange={fieldChange} 
                                showSimpleMessage={shouldShowSimpleModifiedMessage(fieldChange.field, isCreateMode, fieldChange.newValue)} 
                              />
                            ) : (
                              <FieldChangeContentForEdit 
                                fieldChange={fieldChange} 
                                showSimpleMessage={shouldShowSimpleModifiedMessage(fieldChange.field, isCreateMode, fieldChange.newValue)} 
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <DialogFooter className="px-6 py-4 gap-2 sm:gap-0 flex-shrink-0 border-t">
            <Button
              variant="outline"
              onClick={() => setShowSaveDialog(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmSave}
              disabled={isSaving || (!isCreateMode && pendingChanges.length === 0)}
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              {determineConfirmButtonText(isSaving, isCreateMode)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disposal Wizard */}
      {showDisposalWizard && asset && (
        <DisposalWizard
          asset={asset}
          open={showDisposalWizard}
          onClose={() => setShowDisposalWizard(false)}
          onComplete={() => {
            setShowDisposalWizard(false);
            loadAssetDetail();
          }}
        />
      )}

      {/* Label Printing Modal */}
      {showLabelPrintingModal && asset && (
        <LabelPrintingModal
          open={showLabelPrintingModal}
          onClose={() => setShowLabelPrintingModal(false)}
          assets={[asset]}
        />
      )}
    </>
  );
}
